import Controller, { inject as controller } from '@ember/controller';
import { action } from '@ember/object';
import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import type { ParametersForNewEvent } from 'lumen/components/hotel/events/forms/form-edit-event';
import type HotelEventsController from 'lumen/controllers/hotel/events';
import type { SearchResults } from 'lumen/controllers/hotel/events';
import type { Day, Event } from 'lumen/models';
import { EVENTS_STATUSES } from 'lumen/models/event';
import type HotelEventsRoute from 'lumen/routes/hotel/events';
import type AjaxService from 'lumen/services/ajax';

export interface Category {
  icon?: IconName;
  id: string;
  label: string;
}

export default class HotelEventsEditController extends Controller {
  queryParams = ['initialDate'];

  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @controller('hotel.events') declare eventsController: HotelEventsController;

  @tracked initialDate = '';
  @tracked searchResults: SearchResults | undefined;

  get title(): string {
    return this.intl.t('events.new_event');
  }

  private createEvent({
    categoryId,
    description,
    endDate,
    location,
    name,
    placesResponse,
    startDate,
  }: ParametersForNewEvent): Event {
    let beginDate = startDate;
    const days: Day[] = [];

    while (!beginDate.isAfter(endDate)) {
      const day = this.store.peekRecord<Day>(
        'day',
        beginDate.format('YYYY-MM-DD'),
      );

      if (day) {
        days.push(day);
      }

      beginDate = beginDate.add(1, 'd');
    }

    return this.store.createRecord<Event>('event', {
      category: categoryId,
      days,
      description,
      endDate,
      // @ts-expect-error: Type 'Hotel | null' is not assignable to type 'Hotel | undefined'.
      hotel: this.hotelAndMarketService.hotel,
      isNewEvent: true,
      location,
      name,
      placesId: placesResponse?.place_id,
      placesResponse,
      startDate,
      status: EVENTS_STATUSES.ACCEPTED,
    });
  }

  @action
  addExistingEvent(event: Event): void {
    this.ajax
      .request(`/events/${event.id}`, {
        type: 'put',
        data: JSON.stringify({ event: { status: EVENTS_STATUSES.ACCEPTED } }),
      })
      .then(() => {
        this.store.unloadAll('yearoverview');
        this.send('refreshOnClose', true);
        this.toast.success(this.intl.t('events.new_event_added'));
      })
      .catch((error: Error) => {
        this.errorTracking.addBreadcrumb({
          message: `${error.name}  :   ${error.message}`,
        });
        this.toast.alert(this.intl.t('general.error_title'));
      });
  }

  @action
  async fetchSearchResult(input: string): Promise<void> {
    if (input) {
      const result = await this.eventsController?.sendRequest(input, 1, 5);
      if (result) {
        this.searchResults = result;
      }
    } else {
      this.searchResults = undefined;
    }
  }

  @action
  async refreshOnClose(shouldRefetch = false): Promise<void> {
    this.searchResults = undefined;
    this.initialDate = '';

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    await this.router.legacyTransitionTo('hotel.events');

    if (shouldRefetch) {
      const route = getOwner(this)!.lookup(
        'route:hotel.events',
      ) as HotelEventsRoute;
      route.refetchEvents();
    }
  }

  @action
  saveNewEvent(parameters: ParametersForNewEvent): void {
    const event = this.createEvent(parameters);

    event
      .save()
      .then(
        () => {
          this.store.unloadAll('yearoverview');
          this.send('refreshOnClose', true);
          this.toast.success(this.intl.t('events.new_event_added'));
        },
        () => {
          void this.alerting.showErrorAlert();
        },
      )
      .catch((error) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.errorTracking.addBreadcrumb({ data: { error } });
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      });
  }
}
